import { useNavigate } from "#src/Routers/hooks";
import { AddRecordNoteDialog } from "#src/batteries-included-components/Dialogs/AddRecordNoteDialog";
import { EditRecordValueDialog } from "#src/batteries-included-components/Dialogs/EditRecordValueDialog";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneDRVC } from "#src/components/hooks/adapters/useDefaultRecordValueConfig";
import { useGetOneEquipment } from "#src/components/hooks/adapters/useEquipment";
import { useGetOneFacility } from "#src/components/hooks/adapters/useFacilities";
import { useGetOneFlow } from "#src/components/hooks/adapters/useFlows";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { linkToRecordValueDetail } from "#utils/links";
import {
  Banner,
  Button,
  Drawer,
  DrawerProps,
  Icon,
  KeyValueList,
  MetricTile,
  NumberDataDisplay,
  Panel,
} from "@validereinc/common-components";
import {
  AggregationFunctionTypeNames,
  AssetType,
  AssetTypeType,
  DefaultRecordValueConfigurationTypeNames as DRVCTypeNames,
  DefaultRecordValueConfigurationType as DRVCTypesType,
  DefaultRecordValueConfigurationFunctionNames,
  EquipmentType,
  FacilityType,
  FlowType,
  RecordValueConfigurationTypeNames,
  RecordValueType,
  isRecordValueLocked as isRecordValueLockedFunction,
} from "@validereinc/domain";
import {
  formatNumberValueAndUnit,
  monthFormatter,
  toTitleCaseString,
  yearMonthParser,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { useDRVCTargetResolve } from "../DefaultRecordValueConfigurationDrawer/DefaultRecordValueConfigurationDrawer.helpers";
import {
  RecordValueDrawerStateType,
  defaultRecordValueDrawerState,
  useFetchRecordWithEnhancedSources,
  useRecordSourceParser,
} from "./RecordValueDrawer.helper";
import styles from "./RecordValueDrawer.module.scss";

const cx = classNames.bind(styles);

export const RecordValueDrawer = (
  props: Pick<DrawerProps, "onClose"> & {
    assetType: AssetTypeType;
    state?: RecordValueDrawerStateType;
    recordValue?: RecordValueType;
    applyAutomation?: (recordId: string) => void;
    navgivationProps: {
      noPrev: boolean;
      noNext: boolean;
      goNext: () => void;
      goPrev: () => void;
    };
  }
) => {
  const {
    onClose,
    applyAutomation,
    assetType,
    navgivationProps,
    state,
    recordValue,
  } = props;

  const { record_id, year_month, asset_id } =
    state ?? defaultRecordValueDrawerState;
  const { localize } = useLocalization();
  const isOpen = !!state;
  // Disabled state of next/prev buttons + click actions:
  const { noPrev, noNext, goNext, goPrev } = navgivationProps;
  const { getTypeName, getUnitName, getPrecisionByType } =
    useMeasurementTypes();

  const { data: equipmentDetails } = useGetOneEquipment(
    asset_id,
    {},
    { enabled: assetType === AssetType.EQUIPMENT }
  );
  const { data: facilityDetails } = useGetOneFacility(
    asset_id,
    {},
    { enabled: assetType === AssetType.FACILITY }
  );
  const { data: flowData } = useGetOneFlow(
    { id: asset_id },
    { enabled: assetType === AssetType.FLOW }
  );
  const flowDetails = flowData?.data;

  const navigate = useNavigate();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isEditValueModalOpen, setIsEditValueModalOpen] = useState(false);
  const isRecordValueLocked =
    !!recordValue && isRecordValueLockedFunction(recordValue);

  // Fetches the record and injects: estimation_method_entity_id (TODO: fix this in the backend.)
  const { record, isLoading: isRecordLoading } =
    useFetchRecordWithEnhancedSources(record_id);

  const hasDrvc = !!recordValue?.default_record_value_config_id;

  const drvcQuery = useGetOneDRVC(
    { drvcId: recordValue?.default_record_value_config_id ?? "" },
    { enabled: hasDrvc }
  );

  const drvc = drvcQuery.data;

  const {
    isLoading: isDrvcSourceLoading,
    resolvedValues,
    dateRange,
    window,
  } = useDRVCTargetResolve({
    drvc,
  });

  const drvcData = [
    {
      title: "Function",
      value:
        DefaultRecordValueConfigurationFunctionNames[
          drvc?.configuration?.aggregate_function ?? "top"
        ],
    },
    {
      title: "Type",
      value: drvc?.configuration?.configuration_type
        ? DRVCTypeNames[drvc?.configuration?.configuration_type]
        : "Manual",
    },
    ...resolvedValues.map((target: { name: string; url: string }) => ({
      title: "Target",
      value: target.url ? (
        <RoutingLink to={target.url}>{target.name}</RoutingLink>
      ) : (
        target.name
      ),
    })),
    ...(drvc?.configuration?.configuration_type ===
    DRVCTypesType.MEASUREMENT_SERIES
      ? [
          {
            title: "Date Range",
            value: dateRange,
          },
        ]
      : []),
    ...(window
      ? [
          {
            title: "Window",
            value: window,
          },
        ]
      : []),
    {
      title: "Default Value",
      description:
        "Default Value will be used as the record source when automation is applied to a property that 1) does not have an automation configured 2) has an automation configured but a record value has not yet been auto-generated",
      value:
        drvc?.default_value !== undefined
          ? (formatNumberValueAndUnit(
              {
                value: Number(drvc.default_value),
                unit: getUnitName(drvc.measurement_unit),
              },
              {
                overrideOpts: {
                  minimumFractionDigits:
                    getPrecisionByType(drvc.measurement_unit) ?? 0,
                },
              }
            ) ?? "-")
          : "-",
    },
  ];

  const measurementType = recordValue?.measurement_type ?? "";
  const isLoading =
    isRecordLoading ||
    (hasDrvc && drvcQuery.isLoading) ||
    (hasDrvc && isDrvcSourceLoading);

  const sources = useRecordSourceParser(record, measurementType);

  const title = `${
    year_month ? monthFormatter(yearMonthParser(year_month)) : undefined
  } ${getTypeName(measurementType ?? "")}`;

  const precision = getPrecisionByType(measurementType);

  const sourceEquipmentData = (data?: EquipmentType) => [
    {
      title: `${localize("Equipment")}`,
      value: data?.name,
    },
    {
      title: `${localize("Equipment")} Type`,
      value: data?.type.name,
    },
  ];
  const sourceFacilityData = (data?: FacilityType) => [
    {
      title: `${localize("Facility")}`,
      value: data?.name,
    },
  ];
  const sourceFlowData = (data?: FlowType) => [
    {
      title: `${localize("Flow")}`,
      value: data?.name,
    },
    {
      title: `${localize("Flow")} Type`,
      value: toTitleCaseString(data?.type, { intent: "ui" }),
    },
  ];

  const sourceDetailsData = () => {
    const items = [
      {
        title: "Configuration Type",
        value: hasDrvc ? "Default Automation" : "Custom",
      },
      ...sources.map((source) => ({
        title: "Source",
        value: <RoutingLink to={source.url}>{source.name}</RoutingLink>,
      })),
    ];

    if (!hasDrvc) {
      items.push({
        title: "Type",
        value:
          RecordValueConfigurationTypeNames[
            recordValue?.configuration?.configuration_type ?? ""
          ],
      });
      if (recordValue?.configuration.aggregate_function) {
        items.push({
          title: "Function",
          value:
            AggregationFunctionTypeNames[
              recordValue.configuration.aggregate_function
            ],
        });
      }
    }

    return items;
  };

  const actionRow = (
    <div className={cx("drawerActionRow")}>
      <Button
        icon="note-pencil"
        key="add note"
        onClick={() => {
          setIsNoteModalOpen(true);
        }}
        disabled={isRecordValueLocked}
      >
        Add Note
      </Button>
      <Button
        icon="pencil-simple"
        key="edit module"
        onClick={() => {
          setIsEditValueModalOpen(true);
        }}
        disabled={isRecordValueLocked}
      >
        Edit Value
      </Button>
      <Button
        variant="primary"
        icon="play-simple"
        key="record-value-drawer-apply-automation-button"
        onClick={() => {
          if (state?.record_id) applyAutomation?.(state.record_id);
        }}
        disabled={hasDrvc || isLoading || isRecordValueLocked}
      >
        Apply Automation
      </Button>
    </div>
  );

  const navigateToRecordValueDetails = () => {
    navigate({
      pathname: linkToRecordValueDetail(
        assetType,
        asset_id,
        record_id,
        measurementType
      ),
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      size="md"
      onClose={onClose}
      title="Record Preview"
      titleAction={
        <Button onClick={navigateToRecordValueDetails}>
          View Record Details
        </Button>
      }
      actionRow={actionRow}
    >
      <div className={cx("titleContainer")}>
        <h1 className={cx("drawerTitle")}>{title}</h1>
        <div className={cx("buttonsContainer")}>
          <Button
            icon="caret-left"
            disabled={noPrev}
            onClick={goPrev}
            iconPosition="left"
          />
          <Button
            icon="caret-right"
            disabled={noNext}
            onClick={goNext}
            iconPosition="right"
          />
        </div>
      </div>

      {isRecordValueLocked && (
        <Banner
          titleText="This record value is locked"
          variant="generic"
          icon={<Icon variant="lock-simple" />}
          className={cx("lockedBanner")}
        />
      )}

      <Panel
        className={cx("lastUpdatedPanel")}
        isFluidY={false}
      >
        <MetricTile
          title={getTypeName(measurementType ?? "")}
          value={(props) => (
            <NumberDataDisplay
              {...props}
              value={recordValue?.value}
              formattingOpts={
                precision
                  ? {
                      fractionDigits: precision,
                    }
                  : {
                      overrideOpts: {
                        maximumFractionDigits: 3,
                        minimumFractionDigits: 2,
                      },
                    }
              }
            />
          )}
          unit={getUnitName(
            recordValue?.measurement_unit ?? "",
            recordValue?.value ?? undefined
          )}
          className={cx("panelTitleContainer")}
        />

        <div className={cx("sectionTitle")}>Asset Details</div>
        <KeyValueList
          data={
            assetType === AssetType.EQUIPMENT
              ? sourceEquipmentData(equipmentDetails)
              : assetType === AssetType.FLOW
                ? sourceFlowData(flowDetails)
                : sourceFacilityData(facilityDetails)
          }
          variant="shaded"
        />
        <div className={cx("sectionTitle")}>Source Details</div>
        <KeyValueList
          data={sourceDetailsData()}
          variant="shaded"
        />
        {hasDrvc && (
          <>
            <div className={cx("sectionTitle")}>Automation Details</div>
            <KeyValueList
              data={drvcData}
              variant="shaded"
            />
          </>
        )}
      </Panel>
      <AddRecordNoteDialog
        isOpen={isNoteModalOpen}
        recordId={record_id}
        recordValue={recordValue}
        onClose={() => {
          setIsNoteModalOpen(false);
        }}
      ></AddRecordNoteDialog>
      {recordValue && (
        <EditRecordValueDialog
          isOpen={isEditValueModalOpen}
          recordId={record_id}
          recordValue={recordValue}
          onClose={() => {
            setIsEditValueModalOpen(false);
          }}
        />
      )}
    </Drawer>
  );
};
